import React, { forwardRef } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from '@/utils/tailwind';

interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {}
interface TabsTriggerProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {}
interface TabsContentProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> {}

const Tabs = TabsPrimitive.Root;

function TabsList({ className, ...props }: TabsListProps, ref: React.Ref<HTMLDivElement>) {
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn('mb-6 flex w-full border-b-2 border-b-subtle', className)}
      {...props}
    />
  );
}

function TabsTrigger({ className, ...props }: TabsTriggerProps, ref: React.Ref<HTMLButtonElement>) {
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'relative px-4 py-2 text-sm text-subtle disabled:pointer-events-none disabled:opacity-50 data-[state=active]:border-b-gradient data-[state=active]:text-brand-gradient data-[state=active]:after:bg-brand',
        className
      )}
      {...props}
    />
  );
}

function TabsContent({ className, ...props }: TabsContentProps, ref: React.Ref<HTMLDivElement>) {
  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn(
        'ring-offset-background focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        className
      )}
      {...props}
    />
  );
}

const CompoundTabs = Object.assign(Tabs, {
  List: forwardRef<HTMLDivElement, TabsListProps>(TabsList),
  Trigger: forwardRef<HTMLButtonElement, TabsTriggerProps>(TabsTrigger),
  Content: forwardRef<HTMLDivElement, TabsContentProps>(TabsContent)
});

Tabs.displayName = 'TabsUnderline';
TabsList.displayName = 'TabsUnderline.List';
TabsTrigger.displayName = 'TabsUnderline.Trigger';
TabsContent.displayName = 'TabsUnderline.Content';

export { CompoundTabs as TabsUnderline };
